import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { useScenarios } from '../../hooks/useScenarios';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { getCashDurationShortTerm, getCashDurationTerm } from '@/lib/terminology-config';
import { useFormatting } from '@/hooks/useFormatting';

interface ScenarioComparisonModalProps {
  open: boolean;
  onClose: () => void;
}

export const ScenarioComparisonModal: React.FC<ScenarioComparisonModalProps> = ({
  open,
  onClose,
}) => {
  const [selectedScenarios, setSelectedScenarios] = useState<string[]>([]);
  const [showComparison, setShowComparison] = useState(false);
  const { scenarios } = useScenarios();
  const { formatDate, formatCurrency } = useFormatting();

  const toggleScenario = (id: string) => {
    setSelectedScenarios((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleCompare = () => {
    if (selectedScenarios.length >= 2) {
      setShowComparison(true);
    }
  };

  const handleBack = () => {
    setShowComparison(false);
  };

  const selectedScenarioData = scenarios.filter((s) =>
    selectedScenarios.includes(s.id)
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {showComparison ? 'Scenario Comparison' : 'Select Scenarios to Compare'}
          </DialogTitle>
        </DialogHeader>

        {!showComparison ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select 2 or more scenarios to compare side-by-side
            </p>

            <div className="space-y-3">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedScenarios.includes(scenario.id)
                      ? 'border-primary bg-primary/5'
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => toggleScenario(scenario.id)}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedScenarios.includes(scenario.id)}
                      onCheckedChange={() => toggleScenario(scenario.id)}
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{scenario.name}</div>
                        <Badge
                          variant={
                            scenario.label === 'A'
                              ? 'default'
                              : scenario.label === 'B'
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {scenario.label || 'None'}
                        </Badge>
                      </div>
                      <div className="text-sm text-muted-foreground mb-2">
                        {scenario.description}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Min Cash: </span>
                          <span className="font-semibold">
                            {formatCurrency(scenario.metrics.minCash)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">{getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)}: </span>
                          <span className="font-semibold">{getCashDurationTerm(scenario.metrics.runway)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Net Impact: </span>
                          <span className={`font-semibold ${scenario.metrics.netImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {scenario.metrics.netImpact >= 0 ? '+' : ''}
                            {formatCurrency(scenario.metrics.netImpact)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                {selectedScenarios.length} scenario{selectedScenarios.length !== 1 ? 's' : ''}{' '}
                selected
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCompare}
                  disabled={selectedScenarios.length < 2}
                >
                  Compare Selected
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Comparison Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold">Metric</th>
                    {selectedScenarioData.map((scenario) => (
                      <th key={scenario.id} className="text-center p-3">
                        <div className="font-semibold">{scenario.name}</div>
                        <Badge
                          variant={
                            scenario.label === 'A'
                              ? 'default'
                              : scenario.label === 'B'
                              ? 'secondary'
                              : 'outline'
                          }
                          className="mt-1"
                        >
                          {scenario.label || 'None'}
                        </Badge>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Minimum Cash</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center font-semibold">
                        {formatCurrency(scenario.metrics.minCash)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Minimum Date</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center">
                        {formatDate(new Date(scenario.metrics.minCashDate))}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">{getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)}</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center font-semibold">
                        {getCashDurationTerm(scenario.metrics.runway)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Changes</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center">
                        {scenario.changes.length}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Status</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center">
                        <Badge
                          variant={
                            scenario.status === 'active'
                              ? 'default'
                              : scenario.status === 'draft'
                              ? 'secondary'
                              : 'outline'
                          }
                        >
                          {scenario.status}
                        </Badge>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Net Impact</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center">
                        <div className={`font-semibold ${scenario.metrics.netImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {scenario.metrics.netImpact >= 0 ? '+' : ''}
                          {formatCurrency(scenario.metrics.netImpact)}
                        </div>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Ending Cash</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center font-semibold">
                        {formatCurrency(scenario.metrics.endingCash)}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Revenue Change</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center">
                        <div className={scenario.metrics.revenueChange >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {scenario.metrics.revenueChange >= 0 ? '+' : ''}
                          {scenario.metrics.revenueChange}%
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatCurrency(scenario.metrics.revenueChangeAmount)}
                        </div>
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b hover:bg-accent">
                    <td className="p-3 font-medium">Cost Change</td>
                    {selectedScenarioData.map((scenario) => (
                      <td key={scenario.id} className="p-3 text-center">
                        <div className={scenario.metrics.costChange >= 0 ? 'text-red-600' : 'text-green-600'}>
                          {scenario.metrics.costChange >= 0 ? '+' : ''}
                          {scenario.metrics.costChange}%
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatCurrency(scenario.metrics.costChangeAmount)}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Key Insights */}
            <div className="p-4 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h4 className="font-semibold mb-2">Key Insights</h4>
              <div className="space-y-2 text-sm">
                {selectedScenarioData.length >= 2 && (
                  <>
                    <div className="flex items-start gap-2">
                      <TrendingUp className="w-4 h-4 text-green-600 mt-0.5" />
                      <div>
                        <strong>Best Case:</strong>{' '}
                        {
                          selectedScenarioData.reduce((best, s) =>
                            s.metrics.minCash > best.metrics.minCash ? s : best
                          ).name
                        }{' '}
                        maintains highest minimum cash of{' '}
                        {formatCurrency(
                          Math.max(...selectedScenarioData.map((s) => s.metrics.minCash))
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <TrendingDown className="w-4 h-4 text-red-600 mt-0.5" />
                      <div>
                        <strong>Worst Case:</strong>{' '}
                        {
                          selectedScenarioData.reduce((worst, s) =>
                            s.metrics.minCash < worst.metrics.minCash ? s : worst
                          ).name
                        }{' '}
                        shows minimum cash of{' '}
                        {formatCurrency(
                          Math.min(...selectedScenarioData.map((s) => s.metrics.minCash))
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 mt-0.5">📊</div>
                      <div>
                        <strong>{getCashDurationShortTerm().charAt(0).toUpperCase() + getCashDurationShortTerm().slice(1)} Variance:</strong> Scenarios show{' '}
                        {Math.max(...selectedScenarioData.map((s) => s.metrics.runway)) -
                          Math.min(...selectedScenarioData.map((s) => s.metrics.runway))}{' '}
                        months difference in {getCashDurationShortTerm()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-between pt-4 border-t">
              <Button variant="outline" onClick={handleBack}>
                Back to Selection
              </Button>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
